import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Slider,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Add, Edit2, Trash } from "iconsax-react";
import { authGet, authSend, toDraft, isDirty, DEFAULT_ROUTING } from "./routing/routingApi";
import StrategyCards from "./routing/StrategyCards";
import RingBudgetBar from "./routing/RingBudgetBar";
import LiveRingPreview from "./routing/LiveRingPreview";
import RingGroupDialog from "./routing/RingGroupDialog";
import { PALETTE } from "./callsPalette";

function Section({ title, hint, children }) {
  return (
    <Box component="section" sx={{ mb: 4 }}>
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: 15, mb: hint ? 0.25 : 1.25 }}>
        {title}
      </Typography>
      {hint && <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.25 }}>{hint}</Typography>}
      {children}
    </Box>
  );
}

export default function CallsRouting() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [saved, setSaved] = useState(DEFAULT_ROUTING);
  const [draft, setDraft] = useState(DEFAULT_ROUTING);
  const [previewAgents, setPreviewAgents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // withRouting=false reloads only the lists (after a ring group edit) so it can never clobber
  // the admin's unsaved edits to the routing draft.
  const loadAll = useCallback(async (withRouting = true) => {
    try {
      const [groupsRes, routingRes, membersRes, teamsRes] = await Promise.all([
        authGet("/api/calling/ring-groups"),
        withRouting ? authGet("/api/calling/routing") : Promise.resolve(null),
        authGet("/api/teammembers/get"),
        authGet("/api/teams/get"),
      ]);
      setGroups(groupsRes.data || []);
      setTeamMembers(membersRes || []);
      setTeams(teamsRes || []);
      if (routingRes) {
        const fromServer = toDraft(routingRes.routing);
        setSaved(fromServer);
        setDraft(fromServer);
      }
    } catch (err) {
      toast.error("Could not load routing settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = isDirty(draft, saved);
  const staged = draft.strategy !== "simultaneous";
  const needsGroup = draft.mode === "ring_group" && !draft.activeRingGroupId;
  const activeGroup = groups.find((g) => g._id === draft.activeRingGroupId);

  const handleStrategy = (strategy) => {
    if (strategy === "sequential" && draft.mode !== "ring_group") {
      // "Ring in order" walks a ring group's own order — there is no order for "everyone online".
      if (groups.length === 0) {
        toast("Create a ring group first — it defines the order agents ring in.", { icon: "ℹ️" });
        setEditingGroup(null);
        setDialogOpen(true);
        return;
      }
      update({ strategy, mode: "ring_group", activeRingGroupId: groups[0]._id });
      return;
    }
    update({ strategy });
  };

  const handleMode = (_, next) => {
    if (!next) return; // ToggleButtonGroup fires null when the active button is clicked again
    if (next === "all_online") update({ mode: "all_online", activeRingGroupId: "" });
    else update({ mode: "ring_group", activeRingGroupId: draft.activeRingGroupId || groups[0]?._id || "" });
  };

  const handleSave = async () => {
    if (needsGroup) return toast.error("Pick a ring group first");
    setSaving(true);
    try {
      await authSend("post", "/api/calling/routing", {
        mode: draft.mode,
        activeRingGroupId: draft.mode === "ring_group" ? draft.activeRingGroupId : undefined,
        strategy: draft.strategy,
        ringSecondsPerAgent: draft.ringSecondsPerAgent,
        skipBusyAgents: draft.skipBusyAgents,
        whenNobodyAnswers: draft.whenNobodyAnswers,
      });
      setSaved(draft);
      toast.success("Routing saved — applies to the next incoming call");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save routing");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await authSend("delete", `/api/calling/ring-groups/${id}`);
      toast.success("Ring group deleted");
      // The server falls back to "everyone online" (and resets Ring in order) if this group was
      // active — re-read the routing so what's shown matches what's now saved.
      if (saved.activeRingGroupId === id) loadAll(true);
      else {
        if (draft.activeRingGroupId === id) update({ mode: "all_online", activeRingGroupId: "", ...(draft.strategy === "sequential" ? { strategy: "simultaneous" } : {}) });
        loadAll(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete ring group");
    }
  };

  // How many agents the timeline should plan for: the ones that would actually be rung.
  const agentsForBudget = useMemo(() => {
    if (draft.strategy === "smart") return Math.max(previewAgents.filter((a) => a.willRing).length, 0);
    return previewAgents.length || (activeGroup ? (activeGroup.memberIds || []).length : 0);
  }, [draft.strategy, previewAgents, activeGroup]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 880, pb: dirty ? 10 : 2 }}>
      <Section title="How should incoming calls ring?" hint="Pick a style. You can change it any time — it applies to the next call that comes in.">
        <StrategyCards
          value={draft.strategy}
          onChange={handleStrategy}
          disabledHints={{ sequential: draft.mode !== "ring_group" ? "Needs a ring group to define the order" : undefined }}
        />
      </Section>

      <Section title="Who can be rung?" hint={draft.strategy === "smart" ? "Smart distribution picks from this pool." : undefined}>
        <ToggleButtonGroup exclusive size="small" value={draft.mode} onChange={handleMode} aria-label="Who can be rung" sx={{ mb: 1.5 }}>
          <ToggleButton value="all_online" disabled={draft.strategy === "sequential"} sx={{ textTransform: "none", px: 2 }}>
            <Tooltip title={draft.strategy === "sequential" ? "Ring in order needs a ring group" : ""}>
              <span>Everyone online</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="ring_group" sx={{ textTransform: "none", px: 2 }}>
            A ring group
          </ToggleButton>
        </ToggleButtonGroup>

        {draft.mode === "ring_group" && (
          <Box>
            <Select
              size="small"
              value={draft.activeRingGroupId}
              onChange={(e) => update({ activeRingGroupId: e.target.value })}
              displayEmpty
              error={needsGroup}
              sx={{ minWidth: 260 }}
              inputProps={{ "aria-label": "Active ring group" }}
            >
              <MenuItem value="" disabled>
                Select a ring group
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g._id} value={g._id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
            {needsGroup && (
              <Typography sx={{ fontSize: 12.5, color: PALETTE.critical.ring, mt: 0.5 }}>
                {groups.length ? "Choose which ring group should be rung." : "You don't have a ring group yet — create one below."}
              </Typography>
            )}
          </Box>
        )}
      </Section>

      {staged && (
        <Section title="Timing" hint="How long each agent gets before the call moves on.">
          <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, p: 2, bgcolor: "background.paper" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, minWidth: 150 }}>Ring each agent for</Typography>
              <Slider
                value={draft.ringSecondsPerAgent}
                min={5}
                max={30}
                step={1}
                marks={[{ value: 5 }, { value: 10 }, { value: 15 }, { value: 20 }, { value: 25 }, { value: 30 }]}
                onChange={(_, v) => update({ ringSecondsPerAgent: v })}
                valueLabelDisplay="auto"
                aria-label="Seconds to ring each agent"
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <Typography sx={{ fontSize: 14, fontWeight: 700, width: 40, textAlign: "right" }}>{draft.ringSecondsPerAgent}s</Typography>
            </Box>

            <RingBudgetBar agentCount={agentsForBudget} secondsPerAgent={draft.ringSecondsPerAgent} whenNobodyAnswers={draft.whenNobodyAnswers} />
          </Box>

          {draft.strategy === "sequential" && (
            <FormControlLabel
              sx={{ mt: 1.5, display: "flex", alignItems: "flex-start", ml: 0 }}
              control={<Switch checked={draft.skipBusyAgents} onChange={(e) => update({ skipBusyAgents: e.target.checked })} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Skip agents who are on another call</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                    Offline agents are always skipped instantly, so nobody waits out a timer on an empty seat.
                  </Typography>
                </Box>
              }
            />
          )}

          <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2, mb: 0.5 }}>If nobody answers</Typography>
          <RadioGroup value={draft.whenNobodyAnswers} onChange={(e) => update({ whenNobodyAnswers: e.target.value })} aria-label="If nobody answers">
            <FormControlLabel
              value="ring_everyone"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: 14 }}>
                  Ring everyone together <Typography component="span" sx={{ fontSize: 12.5, color: "text.secondary" }}>— last chance before the caller gives up</Typography>
                </Typography>
              }
            />
            <FormControlLabel
              value="let_it_miss"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: 14 }}>
                  Let it go unanswered <Typography component="span" sx={{ fontSize: 12.5, color: "text.secondary" }}>— logged as a missed call</Typography>
                </Typography>
              }
            />
          </RadioGroup>
        </Section>
      )}

      <Section title="Preview">
        <LiveRingPreview draft={draft} onAgentCount={setPreviewAgents} />
      </Section>

      <Section title="Ring groups" hint="A ring group is a named set of agents and teams. Their order here is the order “Ring in order” uses.">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add size={16} color="currentColor" />}
            onClick={() => {
              setEditingGroup(null);
              setDialogOpen(true);
            }}
          >
            New ring group
          </Button>
        </Box>

        {groups.length === 0 && (
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            No ring groups yet — every online agent can be rung until you create one.
          </Typography>
        )}

        {groups.map((g) => {
          const isActive = draft.mode === "ring_group" && draft.activeRingGroupId === g._id;
          return (
            <Box
              key={g._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: (t) => `1px solid ${isActive ? PALETTE.blue.ring : t.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1,
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{g.name}</Typography>
                  {isActive && <Chip size="small" label="In use" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} color="primary" />}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                  {(g.memberIds || []).map((m, i) => (
                    <Chip key={m._id} label={`${i + 1}. ${m.name}`} size="small" />
                  ))}
                  {(g.teamIds || []).map((t) => (
                    <Chip key={t._id} label={t.teamName} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit ${g.name}`}
                  onClick={() => {
                    setEditingGroup(g);
                    setDialogOpen(true);
                  }}
                >
                  <Edit2 size={16} color="currentColor" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" aria-label={`Delete ${g.name}`} onClick={() => handleDelete(g._id)}>
                  <Trash size={16} color="currentColor" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Section>

      <RingGroupDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => loadAll(false)}
        teamMembers={teamMembers}
        teams={teams}
        editingGroup={editingGroup}
      />

      {/* Save bar — only when there's something to save, sticky so it's never scrolled away from. */}
      {dirty && (
        <Box
          role="region"
          aria-label="Unsaved routing changes"
          sx={{
            position: "sticky",
            bottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 2,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "0 8px 24px rgba(0,0,0,.18)",
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>You have unsaved changes</Typography>
          <Button onClick={() => setDraft(saved)} disabled={saving}>
            Discard
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || needsGroup}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
